import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import mEmblem from '../assets/brand/midgard_m_emblem.png';
import api from '../lib/api';
import legalVersions from '../content/legalVersions.json';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_AUTH_ENABLED = GOOGLE_CLIENT_ID.length > 0;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialMode = searchParams.get('mode') === 'login' ? 'login' : 'register';
  
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login, register } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  // Terms acceptance is only asked for once the documents are final and enforced
  const [legal, setLegal] = useState({ enforced: false, versions: {} });
  const [agreed, setAgreed] = useState(false);
  useEffect(() => {
    api.get('/legal/status').then((r) => setLegal(r.data)).catch(() => {});
  }, []);

  const handleGoogleSignIn = () => {
    if (!GOOGLE_AUTH_ENABLED) return;
    const redirectUri = window.location.origin + '/auth/callback';
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back to Midgard!');
        navigate('/home');
      } else {
        if (!username || !displayName) {
          toast.error('Please fill in all fields');
          setLoading(false);
          return;
        }
        if (legal.enforced && !agreed) {
          toast.error('Please accept the Terms of Service and Community Guidelines');
          setLoading(false);
          return;
        }
        const accepted = legal.enforced
          ? { terms: legal.versions.terms, guidelines: legal.versions.guidelines }
          : null;
        await register(username, email, password, displayName, accepted);
        toast.success('Welcome to Midgard! You received 50 Runegold!');
        navigate('/home');
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden" data-testid="auth-page">
      {/* Background: same Yggdrasil artwork with stronger gradient for readability */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source srcSet="/midgard-world-bg.webp" type="image/webp" />
          <img
            src="/midgard-world-bg.jpg"
            alt=""
            className="w-full h-full object-cover object-[60%_30%]"
            loading="eager"
          />
        </picture>
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 60% 50% at 50% 50%, rgba(8,4,18,0.65) 0%, transparent 80%),
              linear-gradient(180deg, rgba(8,4,18,0.5) 0%, rgba(8,4,18,0.4) 50%, rgba(8,4,18,0.7) 100%)
            `,
          }}
        />
      </div>

      {/* Back link */}
      <Link
        to="/"
        className="absolute top-5 left-6 z-30 flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
        data-testid="back-home-link"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back</span>
      </Link>

      {/* Auth Panel */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-20 w-full max-w-md mx-6"
      >
        {/* Panel */}
        <div className="bg-[hsl(264,22%,6%)]/90 backdrop-blur-2xl border border-[hsl(var(--mg-steel)/0.25)] rounded-xl p-8 shadow-2xl" data-testid="auth-panel">
          {/* Brand */}
          <div className="text-center mb-7">
            <div className="flex justify-center mb-4">
              <img
                src={mEmblem}
                alt="Midgard"
                className="w-16 h-16 object-contain"
                style={{ filter: 'drop-shadow(0 0 14px rgba(139,92,246,0.5))' }}
                data-testid="auth-emblem"
              />
            </div>
            <h1 className="mg-wordmark text-white text-2xl mb-1.5" style={{ letterSpacing: '0.14em' }}>Midgard</h1>
            <p className="text-[hsl(var(--mg-text-muted))] text-sm">
              {mode === 'login'
                ? 'Return to the Nine Realms'
                : 'Begin your journey'}
            </p>
          </div>

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={!GOOGLE_AUTH_ENABLED}
            title={!GOOGLE_AUTH_ENABLED ? 'Google sign-in is not yet configured' : undefined}
            className={`mg-btn mg-btn-secondary w-full py-3 mb-5 border-[hsl(var(--mg-steel)/0.3)] transition-opacity ${
              GOOGLE_AUTH_ENABLED
                ? 'text-white/90 cursor-pointer'
                : 'text-white/30 opacity-40 cursor-not-allowed'
            }`}
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {GOOGLE_AUTH_ENABLED ? 'Continue with Google' : 'Google sign-in coming soon'}
          </button>

          {/* Divider */}
          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ height: '1px', background: 'linear-gradient(90deg, transparent, hsl(var(--mg-steel)/0.35), transparent)' }} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 text-xs uppercase tracking-widest text-[hsl(var(--mg-text-muted))] bg-[hsl(var(--mg-surface))]">or continue with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="displayName" className="text-xs text-[hsl(var(--mg-text-muted))] uppercase tracking-wider">Display Name</Label>
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Your Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="mg-input h-11"
                    required
                    data-testid="display-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="username" className="text-xs text-[hsl(var(--mg-text-muted))] uppercase tracking-wider">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="mg-input h-11"
                    required
                    data-testid="username-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-[hsl(var(--mg-text-muted))] uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mg-input h-11"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-[hsl(var(--mg-text-muted))] uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mg-input h-11 pr-11"
                  required
                  minLength={mode === 'register' ? 8 : undefined}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--mg-text-muted))] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div className="space-y-2 text-[13px] text-[hsl(var(--mg-text-muted))]" data-testid="register-legal">
                <p className="text-[12px]">At least 8 characters.</p>
                {legal.enforced ? (
                  <label className="flex items-start gap-2.5 cursor-pointer text-white/85">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[hsl(var(--mg-purple))]"
                      required
                      data-testid="accept-terms-checkbox"
                    />
                    <span>
                      I agree to the{' '}
                      <a href="/legal/terms" target="_blank" rel="noopener" className="text-[hsl(var(--mg-purple))] underline">Terms of Service</a>
                      {' '}and the{' '}
                      <a href="/legal/guidelines" target="_blank" rel="noopener" className="text-[hsl(var(--mg-purple))] underline">Community Guidelines</a>.
                    </span>
                  </label>
                ) : (
                  <p>
                    Midgard's{' '}
                    <a href="/legal/terms" target="_blank" rel="noopener" className="text-[hsl(var(--mg-purple))] underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/legal/guidelines" target="_blank" rel="noopener" className="text-[hsl(var(--mg-purple))] underline">Community Guidelines</a>
                    {' '}are being finalised. Read the {legalVersions.status === 'draft' ? 'drafts' : 'current versions'} now; you will be asked to accept them when they take effect.
                  </p>
                )}
                <p>
                  How we handle your data:{' '}
                  <a href="/legal/privacy" target="_blank" rel="noopener" className="text-[hsl(var(--mg-purple))] underline">Privacy Policy</a>.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (mode === 'register' && legal.enforced && !agreed)}
              className="mg-btn mg-btn-primary w-full py-3 text-base mt-2"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                mode === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* Toggle mode */}
          <div className="mt-6 text-center">
            <p className="text-[hsl(var(--mg-text-muted))] text-sm">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
              {' '}
              <button
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="text-[hsl(var(--mg-purple))] font-semibold hover:underline"
                data-testid="toggle-mode-btn"
              >
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
